import { H6 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';

import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import { useAppSelector } from '@/redux/hooks';
import { getUserIsInGoodStanding } from '@/redux/modules/account/user/user.selectors';
import { getWonItem } from '@/redux/modules/bidder/biddingInfoAll/bidderBiddingInfoAll.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderHasHighBid,
    getItemSummaryBidderWonItem,
    getItemSummaryCatalogId,
    getItemSummaryIsAvailable,
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsPlacingBid,
    getItemSummaryIsReserveMet,
    getItemSummaryIsTimedPlusAuction,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

type Props = {
    alignBannerBottomWithGap?: boolean;
    alignBannerToBottom?: boolean;
    itemId: number;
};

const bannerBaseStyles =
    'absolute z-10 h-[22px] w-full max-w-[calc(100%-16px)] cursor-default select-none text-center font-secondary text-xs leading-[22px] tracking-[0.5px] text-white shadow-[0_0_8px_0_rgb(var(--color-surface-200)/50%)] bottom-sm print:!hidden';

const CardStatusBannerComponent = ({ alignBannerBottomWithGap, alignBannerToBottom = false, itemId }: Props) => {
    const formatMessage = useAppFormatMessage();
    const isMobile = useIsMobileLayout();

    let banner = null;
    let bannerBackground = '';

    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderHasHighBid = useAppSelector((state) => getItemSummaryBidderHasHighBid(state, itemId));
    const bidderHasPlacedBid = useAppSelector((state) => getItemSummaryIsPlacingBid(state, itemId));
    const bidderWonItem = useAppSelector((state) => getItemSummaryBidderWonItem(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const isAvailable = useAppSelector((state) => getItemSummaryIsAvailable(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemSummaryIsReserveMet(state, itemId));
    const isTimedPlusAuction = useAppSelector((state) => getItemSummaryIsTimedPlusAuction(state, itemId));

    const accountInGoodStanding = useAppSelector(getUserIsInGoodStanding);
    const bidderHasWon = useAppSelector((state) => getWonItem(state, itemId)) || bidderWonItem;

    const { blockedByAuctioneer, declined, pending, suspended } = useAppSelector((state) =>
        getCatalogRegistration(state, catalogId)
    );

    if (suspended || !accountInGoodStanding) {
        banner = formatMessage(['account_suspended']);
        bannerBackground = 'bg-surface-300/80';
    } else if (blockedByAuctioneer || declined) {
        banner = formatMessage(['registration_declined']);
        bannerBackground = 'bg-surface-300/80';
    } else if (isAvailable) {
        if (bidderHasHighBid && isReserveMet) {
            if (pending) {
                banner = (
                    <H6 className="font-medium uppercase">
                        {isMobile ? formatMessage(['pending']) : formatMessage(['bid_registration_pending'])}
                    </H6>
                );
                bannerBackground = 'bg-surface-500';
            } else {
                banner = <H6 className="font-medium uppercase">{formatMessage(['leading'])}</H6>;
                bannerBackground = 'bg-success-700';
            }
        } else if (bidderHasHighBid && !isReserveMet) {
            banner = <H6 className="font-medium uppercase">{formatMessage(['reserve_not_met'])}</H6>;
            bannerBackground = 'bg-critical-500';
        } else if (!bidderHasHighBid && bidderHasBid && !bidderHasPlacedBid) {
            banner = <H6 className="font-medium uppercase">{formatMessage(['outbid'])}</H6>;
            bannerBackground = 'bg-critical-400';
        }
    } else if (bidderHasWon && !isPassed) {
        banner = <H6 className="font-medium">{formatMessage(['you_won_new_banner'])}</H6>;
        bannerBackground = 'bg-success-700';
    } else if (isPassed && !isTimedPlusAuction && pending) {
        banner = formatMessage(['not_approved']);
        bannerBackground = 'bg-surface-300/80';
    } else if (bidderHasBid && !isPassed) {
        banner = <H6 className="font-medium">{formatMessage(['lost_new_banner'])}</H6>;
        bannerBackground = 'bg-surface-300/80';
    }

    if (!banner || (isTimedPlusAuction && isPaused)) {
        return null;
    }

    return (
        <div
            className={cn(
                bannerBaseStyles,
                bannerBackground,
                alignBannerBottomWithGap && 'bottom-xs xsMax:bottom-0 xsMax:left-0 xsMax:max-w-full',
                alignBannerToBottom && 'bottom-0 xsMax:max-w-full'
            )}
            data-testid="banner-div"
        >
            {banner}
        </div>
    );
};

export const CardStatusBanner = memo<Props>(CardStatusBannerComponent);
